/**
 * Automatic Page View Tracking Hook
 *
 * Use this hook in any page component to automatically track page views.
 * It tracks when the component mounts and sends the page path to Google Analytics.
 */

"use client"

import { trackPageView } from "@/lib/analytics"
import { usePathname } from "next/navigation"
import { useEffect } from "react"

/**
 * Centralised path → human-readable title map.
 * Exported so other modules (e.g. the analytics dashboard) can reuse it.
 */
export const PAGE_TITLES: Record<string, string> = {
  "/": "Stream Stats Dashboard",
  "/login": "Login",
  "/slots/library": "Slots Library",
  "/live-casino/library": "LC Library",
  "/roadmaps": "Roadmaps",
  "/crash-games": "Crash Games",
  "/slots/assets": "Slots Assets",
  "/live-casino/assets": "Live Casino Assets",
  "/crash-games/assets": "Crash Games Assets",
  "/news": "News",
  "/challenges": "Challenges",
}

/**
 * Hook to automatically track page views
 * @param pageTitle - Optional custom page title (overrides the default from PAGE_TITLES)
 */
export function usePageTracking(pageTitle?: string) {
  const pathname = usePathname()

  useEffect(() => {
    const title = pageTitle ?? PAGE_TITLES[pathname]
    trackPageView(pathname, title)
  }, [pathname, pageTitle])
}

/**
 * Predefined page tracking for common pages
 */
export const PageTracking = {
  useLoginPage() {
    usePageTracking("Login")
  },

  useStreamStats() {
    usePageTracking("Stream Stats Dashboard")
  },

  useSlotsLibrary() {
    usePageTracking("Slots Library")
  },

  useRoadmaps() {
    usePageTracking("Roadmaps")
  },

  useSlotsAssets() {
    usePageTracking("Slots Assets")
  },

  useLiveCasinoLibrary() {
    usePageTracking("LC Library")
  },

  useLiveCasinoAssets() {
    usePageTracking("Live Casino Assets")
  },

  useCrashGamesLibrary() {
    usePageTracking("Crash Games Library")
  },

  useCrashGamesAssets() {
    usePageTracking("Crash Games Assets")
  },

  useAdminDashboard() {
    usePageTracking("Admin Dashboard")
  },

  useManageStreamers() {
    usePageTracking("Manage Streamers")
  },

  useManageAdmins() {
    usePageTracking("Manage Admins")
  },

  useReports() {
    usePageTracking("Reports")
  },
}
